<template>
  <div class="controls-bar">
    <FilterButton
      button-class="button-light map-filter-button"
      :filter="datasetFilters"
      :filter-count="activeFilterCount"
      @click="showFilterDialog = !showFilterDialog"
      @clear-filters="clearAllFilters"
    >
      Filter
      <SVGIcon
        :iconName="'expand_more'"
        :width="'18px'"
        :height="'18px'"
      />
    </FilterButton>
  </div>
  <Teleport to="body">
    <Transition name="filter-drawer">
      <div v-if="showFilterDialog" class="filter-drawer-backdrop" @mousedown.self="showFilterDialog = false">
        <div class="filter-drawer">
          <div class="filter-drawer__header">
            <div>
              <h2 class="mt-0 mb-0">Filters</h2>
              <p class="filter-drawer__count">{{ imageList.length }} images shown</p>
            </div>
            <div class="filter-drawer__header-actions">
              <button v-if="activeFilterCount > 0" class="clear-filters-btn" @click="clearAllFilters">
                Clear All
              </button>
              <IconButton
                :icon="'close'"
                :width="28"
                :height="28"
                @click="showFilterDialog = false"
              />
            </div>
          </div>
          <div class="filter-drawer__content scrollbar">
            <DatasetFilter
              v-model="datasetFilters"
              :dataset="currentDataset"
              :annotationSets="annotationSets"
              :labels="currentLabels"
              :showCopyFilteredImagesBtn="false"
              :types="currentTypes"
              :collapsible="false"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
  <div id="map" />
</template>

<script>
import DatasetFilter from '@/components/DatasetComponent/DatasetFilters/DatasetFilter.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import IconButton from '@/components/IconButton.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import FilterButton from '@/components/FilterButton.vue';
import { Map, View, Feature } from 'ol';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { Cluster, OSM, Vector as VectorSource } from 'ol/source';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import { boundingExtent } from 'ol/extent';
import { Select } from 'ol/interaction';
import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
  Text,
} from 'ol/style';
import 'ol/ol.css';

export default {
  name: 'Map',
  components: {
    DatasetFilter,
    IconButton,
    SVGIcon,
    FilterButton,
  },
  props: {
    show: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      map: null,
      markersLayer: null,
      markersSource: null,
      clusterSource: null,
      imageList: [],
      imagesLimit: 10000,
      datasetFilters: {},
      showFilterDialog: false,
      controller: new AbortController(),
    };
  },
  computed: {
    activeFilterCount() {
      const filters = this.datasetFilters;
      const hasBoundingBoxSizeFilter = Boolean(
        filters?.boundingBoxSizeFilter?.lower_height
        || filters?.boundingBoxSizeFilter?.lower_width
        || filters?.boundingBoxSizeFilter?.upper_height
        || filters?.boundingBoxSizeFilter?.upper_width,
      );

      return [
        filters?.groupsFilter?.length > 0,
        filters?.imageNameFilter?.length > 0,
        Boolean(filters?.timeRangeFilter),
        Boolean(filters?.dateRangeFilter),
        filters?.cameraSourceFilter?.length > 0,
        Boolean(filters?.confidenceRangeFilter),
        filters?.annotationTypeFilter?.length > 0,
        filters?.annotationSetFilter?.length > 0,
        filters?.imagesWithAnnotationSetFilter?.length > 0,
        filters?.annotationLabelFilter?.length > 0,
        hasBoundingBoxSizeFilter,
        filters?.imageTagFilter?.length > 0,
      ].filter(Boolean).length;
    },
    datasets() {
      return this.$store.state.datasets.datasetList;
    },
    currentDataset() {
      return this.$store.state.datasets.currentDataset;
    },
    getImagesParams() {
      if (!this.currentDataset) {
        return null;
      }

      const params = {
        images_filter: { dataset_id: this.currentDataset.id },
        image_files_filter: {},
        annotations_filter: {},
        sensors_filter: {},
      };

      if (this.imagesLimit) {
        params.limit = this.imagesLimit;
      }

      // Images filter
      if (this.datasetFilters.groupsFilter && this.datasetFilters.groupsFilter.length > 0) {
        params.images_filter.group_ids = this.datasetFilters.groupsFilter;
      }

      // Annotations filter
      if (this.datasetFilters.confidenceRangeFilter) {
        params.annotations_filter.lower_score = this.datasetFilters.confidenceRangeFilter[0];
        params.annotations_filter.upper_score = this.datasetFilters.confidenceRangeFilter[1];
      }
      if (this.datasetFilters.annotationTypeFilter && this.datasetFilters.annotationTypeFilter.length > 0) {
        params.annotations_filter.types = this.datasetFilters.annotationTypeFilter;
      }
      if (this.datasetFilters.annotationSetFilter && this.datasetFilters.annotationSetFilter.length > 0) {
        params.annotations_filter.annotation_set_ids = this.datasetFilters.annotationSetFilter.map((set) => set.id);
      }
      if (this.datasetFilters.imagesWithAnnotationSetFilter && this.datasetFilters.imagesWithAnnotationSetFilter.length > 0) {
        params.annotations_filter.images_with_annotation_set_ids = this.datasetFilters.imagesWithAnnotationSetFilter.map((set) => set.id);
      }
      if (this.datasetFilters.annotationLabelFilter && this.datasetFilters.annotationLabelFilter.length > 0) {
        params.annotations_filter.images_with_annotation_label_indexes = this.datasetFilters.annotationLabelFilter;
      }
      if (this.datasetFilters.boundingBoxSizeFilter) {
        Object.entries(this.datasetFilters.boundingBoxSizeFilter).forEach(([key, value]) => {
          if (key !== 'type' && value) {
            params.annotations_filter[`${key}_${this.datasetFilters.boundingBoxSizeFilter.type}`] = value;
          }
        });
      }

      // Image files filter
      if (this.datasetFilters.imageNameFilter && this.datasetFilters.imageNameFilter.length > 0) {
        params.image_files_filter.image_name = this.datasetFilters.imageNameFilter;
      }
      if (this.datasetFilters.timeRangeFilter) {
        params.image_files_filter.lower_time = this.datasetFilters.timeRangeFilter[0];
        params.image_files_filter.upper_time = this.datasetFilters.timeRangeFilter[1];
      }
      if (this.datasetFilters.dateRangeFilter) {
        if (this.datasetFilters.dateRangeFilter[0]) {
          params.image_files_filter.lower_date = new Date(this.datasetFilters.dateRangeFilter[0]).toISOString();
        }
        if (this.datasetFilters.dateRangeFilter[1]) {
          params.image_files_filter.upper_date = new Date(this.datasetFilters.dateRangeFilter[1]).toISOString();
        }
      }
      if (this.datasetFilters.cameraSourceFilter && this.datasetFilters.cameraSourceFilter.length > 0) {
        params.image_files_filter.image_sources = this.datasetFilters.cameraSourceFilter;
      }

      if (Object.keys(params.annotations_filter).length === 0) {
        delete params.annotations_filter;
      }

      return params;
    },
    currentLabels() {
      return this.currentDataset.labels || [];
    },
    currentTypes() {
      if (this.currentDataset) {
        return this.currentDataset.annotation_types;
      }
      return [];
    },
    annotationSets: {
      get() {
        let sets = [];
        if (this.currentDataset) {
          sets = this.datasets.find((d) => d.id === this.currentDataset.id).annotation_sets;
        }
        if (!sets || (sets && sets.length === 0)) {
          this.getAnnotationSetsForDataset(this.currentDataset);
        }

        return sets;
      },
      set(sets) {
        const index = this.datasets.indexOf(this.datasets.find((d) => d.id === this.currentDataset.id));
        this.$store.commit('datasets/setDatasetAnnotationSets', { index, sets });
      },
    },
  },
  watch: {
    show() {
      this.$nextTick(() => {
        this.map.updateSize();
      });
    },
    imageList: {
      handler(imageList) {
        this.addMarker(imageList);
      },
    },
    getImagesParams: {
      deep: true,
      async handler(params) {
        this.imageListPromise = new Promise((resolve, reject) => {
          this.getImages(params).then((imageList) => {
            this.imageList = imageList;
            resolve(imageList);
          });
        });
      },
    },
  },
  async created() {
    if (this.getImagesParams) {
      this.imageList = await this.getImages(this.getImagesParams);
    }
  },
  mounted() {
    this.map = new Map({
      target: 'map',
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: [0, 0],
        zoom: 2,
      }),
    });

    if (this.markersSource) {
      this.markersSource.clear();
    }
  },
  methods: {
    isAbortError(error) {
      const message = error?.message || error?.toString?.() || '';
      return message.includes('AbortError') || message.includes('signal is aborted');
    },
    clearAllFilters() {
      this.datasetFilters.groupsFilter = [];
      this.datasetFilters.imageNameFilter = [];
      this.datasetFilters.timeRangeFilter = null;
      this.datasetFilters.dateRangeFilter = null;
      this.datasetFilters.cameraSourceFilter = [];
      this.datasetFilters.confidenceRangeFilter = null;
      this.datasetFilters.annotationTypeFilter = [];
      this.datasetFilters.annotationTypeFilterInvert = false;
      this.datasetFilters.annotationSetFilter = [];
      this.datasetFilters.imagesWithAnnotationSetFilter = [];
      this.datasetFilters.annotationLabelFilter = [];
      this.datasetFilters.boundingBoxSizeFilter = null;
      this.datasetFilters.imageTagFilter = [];
      this.datasetFilters.displayAnnotationsFilter = null;
    },
    async getLabelListForDataset(dataset) {
      const resp = await this.dataConnect.getLabelList({ dataset_id: dataset.id })
        .catch((error) => {
          console.log('Failed to retrieve labels:', error);
        });
      if (resp && !resp.error && resp.result) {
        this.currentLabels = resp.result;
      }
    },
    async getAnnotationSetsForDataset(dataset) {
      const resp = await this.dataConnect.getAnnotationSets({
        dataset_id: dataset.id,
        get_num_annotations: true,
        get_num_images: true,
        get_labels: true,
      })
        .catch((error) => {
          console.log('Failed to retrieve sets:', error);
        });
      if (resp && !resp.error && resp.result) {
        this.annotationSets = resp.result;
      }
    },
    async getImages(params) {
      this.controller.abort();
      this.controller = new AbortController();
      const currentController = this.controller;

      const resp = await this.dataConnect.getFilteredImages(params, currentController.signal)
        .catch((error) => {
          if (currentController.signal.aborted || this.isAbortError(error)) {
            return this.imageList;
          }
          console.log(error);
          alert(error);
          return this.imageList;
        });
      if (!resp || resp.error || !resp.result) {
        if (currentController.signal.aborted || this.isAbortError(resp?.error)) {
          return this.imageList;
        }
        if (resp && resp.error) {
          console.error(resp.error);
          alert(resp.error.message);
        }
        return this.imageList;
      }

      return resp.result;
    },
    addMarker(images) {
      if (this.markersSource) {
        this.markersSource.clear();
      }
      const features = [];
      images.forEach((img) => {
        features.push(
          new Feature({
            geometry: new Point(fromLonLat([img.longitude, img.latitude])),
          }),
        );
      });
      this.markersSource = new VectorSource({ features });

      const clusterDistance = 5;
      const minDistanceBetweenClusters = 50;
      this.clusterSource = new Cluster({
        distance: clusterDistance,
        minDistance: minDistanceBetweenClusters,
        source: this.markersSource,
      });

      const styleCache = {};
      this.markersLayer = new VectorLayer({
        source: this.clusterSource,
        style(feature) {
          const size = feature.get('features').length;
          let style = styleCache[size];
          if (!style) {
            style = new Style({
              image: new CircleStyle({
                radius: 7,
                fill: new Fill({
                  color: '#ff4343',
                }),
              }),
              // text: new Text({
              //   text: size.toString(),
              //   fill: new Fill({
              //     color: '#000',
              //   }),
              // }),
            });
            styleCache[size] = style;
          }
          return style;
        },
      });

      this.map.addLayer(this.markersLayer);
    },
  },
};
</script>

<style lang="scss" scoped>

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
}
.controls-bar {
  display: flex;
  flex-direction: row;
  padding: 10px 20px 10px 20px;
  align-items: center;
  border-bottom: 1px solid #c9c9c9;
  @include themify() {
    background: themed('color-ribbon');
  }
  justify-content: space-between;
}

.map-filter-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.actions-badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 700;
}

.filter-drawer-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1300;
  display: flex;
  justify-content: flex-end;
}

.filter-drawer {
  width: 300px;
  max-width: 100vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
  background: #f5f5f5;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #c9c9c9;
    flex-shrink: 0;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__count {
    margin: 4px 0 0 0;
    font-size: 12px;
    color: #666;
    font-weight: normal;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;

    :deep(.filters) {
      flex-direction: column;
      flex-wrap: nowrap;
    }

    :deep(.filters__active-and-default-filters) {
      flex-direction: column;
      flex-wrap: nowrap;

      > * {
        width: 100%;
      }
    }

    :deep(.filter-button) {
      width: 100% !important;
      max-width: 100% !important;
    }
  }
}

.clear-filters-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #999;
  border-radius: 4px;
  background: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #e8e8e8;
    border-color: #666;
  }

  &:active {
    opacity: 0.8;
  }
}

.filter-drawer-enter-active,
.filter-drawer-leave-active {
  transition: opacity 0.25s ease;

  .filter-drawer {
    transition: transform 0.25s ease;
  }
}

.filter-drawer-enter-from,
.filter-drawer-leave-to {
  opacity: 0;

  .filter-drawer {
    transform: translateX(100%);
  }
}

#map {
  height: 100%;
  padding: 20px;
}

@media (max-width: 480px) {
  .filter-drawer {
    width: 100vw;
  }
}
</style>
